"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ArrowRight } from "lucide-react";
function ProductCardsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        {Array.from({
        length: 5
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-2xl border border-border bg-card p-4 animate-pulse">
            <div className="aspect-[4/5] w-full rounded-xl bg-muted" />
            <div className="mt-4 h-4 w-1/3 rounded bg-muted" />
            <div className="mt-2 h-6 w-3/4 rounded bg-muted" />
            <div className="mt-2 h-4 w-1/4 rounded bg-muted" />
            <div className="mt-6 h-10 w-full rounded-full bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No pudimos cargar los productos en este momento.
        </p>
        <button type="button" onClick={() => refetch()} className="mt-3 text-sm font-semibold text-primary underline underline-offset-4">
          Reintentar
        </button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No hay productos disponibles por ahora.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const isKit = product.title.toLowerCase().includes("kit") || product.tags?.some(tag => tag.slug?.includes("mejor-valor") || tag.name?.toLowerCase().includes("mejor valor"));
      const categoryName = product.categories && product.categories.length > 0 ? product.categories[0].name : "Ritual";
      return <article key={product.id} data-index={i} className={`group relative flex flex-col justify-between rounded-2xl border bg-card p-4 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md ${isKit ? "border-primary/30 sm:col-span-2 lg:col-span-1" : "border-border"}`}>
            <div>
              {/* Product Image Box */}
              <div className="relative overflow-hidden rounded-xl bg-secondary/40">
                <Link to={product.link} className="block focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-xl" aria-label={`Ver detalles de ${product.title}`}>
                  <AspectRatio ratio={4 / 5} className="overflow-hidden">
                    {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-102" loading="lazy" decoding="async" /> : <div className="flex h-full w-full items-center justify-center bg-muted/60 text-muted-foreground text-sm">
                        Spora
                      </div>}
                  </AspectRatio>
                </Link>
              </div>

              {/* Product Info */}
              <div className="mt-4 flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-secondary/80 px-2.5 py-0.5 text-xs font-medium text-secondary-foreground uppercase tracking-wider">
                    {categoryName}
                  </span>
                  <ProductPrice product={product} className="text-sm font-semibold text-foreground" />
                </div>

                <Link to={product.link} className="group/title inline-block focus:outline-hidden">
                  <h3 className="font-serif text-lg font-semibold text-foreground transition-colors group-hover/title:text-primary">
                    {product.title}
                  </h3>
                </Link>

                {product.short_description && <div className="line-clamp-2 text-sm text-muted-foreground [&_p]:mb-0 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="mt-6 flex flex-col gap-2 pt-2 border-t border-border/60">
              <AddToCartButton product={product} productId={product.id} className="w-full" buttonClassName="w-full rounded-full bg-primary text-primary-foreground font-semibold py-2.5 shadow-xs hover:bg-primary/90 transition-colors" text="Añadir al carrito" showQuantity={false} />
              <Link to={product.link} className="inline-flex items-center justify-center gap-1.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground">
                <span>Conocer ingredientes y ritual</span>
                <ArrowRight className="size-3" />
              </Link>
            </div>
          </article>;
    })}
    </div>;
}
const wp_query_params = {
  per_page: 4,
  order: "asc",
  orderby: "menu_order",
  exclude: [22]
};
const wp_query = new WP_Query(wp_query_params);
export default function Catalog() {
  return <section id="grid-de-productos" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-(--breakpoint-xl) px-5 sm:px-8 lg:px-14">
        {/* Header Block */}
        <div className="mb-12 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            <span className="size-1.5 rounded-full bg-primary" />
            Nuestra colección
          </div>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Fórmulas micelares y extractos puros
          </h2>
          <p className="mt-3 text-base text-muted-foreground sm:text-lg">
            Hongos funcionales y botánica viva combinados para cuidar tu piel y
            tu bienestar desde adentro.
          </p>
        </div>

        {/* Dynamic Products Grid Provider */}
        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        order: "asc",
        orderby: "menu_order",
        exclude: [22]
      }}>
          <ProductCardsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}